import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiGet, apiPostJson, apiPutJson, apiDelete, apiDownload, ApiError } from '../api/client';
import { BUSINESSES, businessBySlug } from '../domain/businesses';
import { formatNumber } from '../lib/format';
import { btn } from '../components/FilterBar';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';

/**
 * Nënkategoritë — the article catalogues behind expenses and revenue.
 * Ports frmNenKategorit and the four frmNenkategorit* forms.
 *
 * The two catalogues have different keys: expense articles belong to a
 * (category, business) pair, revenue articles only to a business. One screen,
 * two modes.
 */

interface ExpenseArticle {
  id: number;
  artikulli: string;
  kategoriaID: number;
  kategoria: string | null;
  usageCount: number;
}

interface RevenueArticle {
  id: number;
  artikulli: string;
  usageCount: number;
}

interface Category {
  id: number;
  kategoria: string;
}

const REVENUE_SLUGS = ['stargym', 'missfit', 'starbar'];

export default function Nenkategorit({ kind }: { kind: 'shpenzimet' | 'fitimet' }) {
  const { biznesi = 'stargym' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const business = businessBySlug(biznesi);
  const slugs = kind === 'fitimet' ? REVENUE_SLUGS : BUSINESSES.map((b) => b.slug);

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<{ id: number; name: string; usage: number } | null>(null);

  useEffect(() => setSearch(''), [biznesi, kind]);

  // Revenue has no Stargroup catalogue; bounce rather than 400.
  useEffect(() => {
    if (kind === 'fitimet' && !REVENUE_SLUGS.includes(biznesi)) {
      navigate('/nenkategorit/fitimet/stargym', { replace: true });
    }
  }, [kind, biznesi, navigate]);

  const dataQuery = useQuery({
    queryKey: ['nenkategorit', kind, biznesi, search],
    queryFn: () =>
      apiGet<{
        business: { slug: string; name: string };
        categories?: Category[];
        articles: (ExpenseArticle | RevenueArticle)[];
      }>(`/nenkategorit/${kind}/${biznesi}`, { search }),
    enabled: Boolean(business) && (kind === 'shpenzimet' || REVENUE_SLUGS.includes(biznesi)),
    placeholderData: (prev) => prev,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['nenkategorit'] });
  }

  const deleteMutation = useMutation({
    mutationFn: ({ id, force }: { id: number; force: boolean }) =>
      apiDelete(`/nenkategorit/${kind}/${biznesi}/${id}${force ? '?force=true' : ''}`),
    onSuccess: () => {
      toast.success('Artikulli u fshi.');
      refresh();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Fshirja dështoi.'),
  });

  const categories = dataQuery.data?.categories ?? [];
  const articles = dataQuery.data?.articles ?? [];

  function openEdit(article: (ExpenseArticle & RevenueArticle) | null) {
    setFormError(null);
    setName(article?.artikulli ?? '');
    setCategoryId(article?.kategoriaID ?? (categories[0]?.id ?? ''));
    setEditing({ id: article?.id ?? null });
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (!name.trim()) {
      setFormError('Ju lutem vendosni emrin e artikullit.');
      return;
    }
    if (kind === 'shpenzimet' && categoryId === '') {
      setFormError('Ju lutem zgjidhni një kategori.');
      return;
    }

    const body =
      kind === 'shpenzimet'
        ? { artikulli: name.trim(), kategoriaId: categoryId }
        : { artikulli: name.trim() };

    try {
      let movedTotal = 0;
      if (editing?.id) {
        // Moving an expense article to another category re-files its history
        // with it, as the desktop did (frmNenkategoritShpenzimetAdd.cs:181).
        // That can touch a lot of rows, so it is reported rather than silent.
        const result = await apiPutJson<{ movedTotal?: number }>(
          `/nenkategorit/${kind}/${biznesi}/${editing.id}`,
          body,
        );
        movedTotal = result?.movedTotal ?? 0;
      } else {
        await apiPostJson(`/nenkategorit/${kind}/${biznesi}`, body);
      }
      setEditing(null);
      toast.success(
        movedTotal > 0
          ? `Të dhënat u ruajtën. ${movedTotal} rreshta u zhvendosën në kategorinë e re.`
          : 'Të dhënat u ruajtën me sukses!',
      );
      refresh();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Ruajtja dështoi.');
    }
  }

  if (!business) {
    return <div className="rounded-xl bg-white p-6 text-red-700">Biznesi nuk u gjet.</div>;
  }

  const inputClass =
    'w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100';

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">
            Nënkategoritë — {kind === 'shpenzimet' ? 'shpenzimet' : 'fitimet'}
          </h1>
          <p className="text-sm text-muted">
            Artikujt që zgjidhen kur shtohet një {kind === 'shpenzimet' ? 'shpenzim' : 'fitim'}
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/nenkategorit/shpenzimet/stargym"
            className={kind === 'shpenzimet' ? btn.primary : btn.secondary}
          >
            Shpenzimet
          </Link>
          <Link
            to="/nenkategorit/fitimet/stargym"
            className={kind === 'fitimet' ? btn.primary : btn.secondary}
          >
            Fitimet
          </Link>
        </div>
      </div>

      <div className="rounded-xl bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap gap-2">
            {slugs.map((slug) => (
              <Link
                key={slug}
                to={`/nenkategorit/${kind}/${slug}`}
                className={slug === biznesi ? btn.primary : btn.secondary}
              >
                {businessBySlug(slug)?.name ?? slug}
              </Link>
            ))}
          </div>

          <div className="min-w-52 flex-1">
            <label className="mb-1 block text-xs font-medium text-muted" htmlFor="nk-search">
              Kërko artikull
            </label>
            <input
              id="nk-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Shkruani për të filtruar…"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          {kind === 'shpenzimet' && (
            <button
              type="button"
              onClick={() =>
                apiDownload(`/nenkategorit/shpenzimet/${biznesi}/eksport`).catch((err) =>
                  toast.error(err instanceof ApiError ? err.message : 'Eksportimi dështoi.'),
                )
              }
              disabled={articles.length === 0}
              className={btn.secondary}
            >
              Eksporto Excel
            </button>
          )}
          <button type="button" onClick={() => openEdit(null)} className={btn.primary}>
            Shto artikull
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                {kind === 'shpenzimet' && <th className="px-4 py-3 font-semibold">Kategoria</th>}
                <th className="px-4 py-3 font-semibold">Artikulli</th>
                <th className="px-4 py-3 text-right font-semibold">Përdorur në</th>
                <th className="px-4 py-3 text-right font-semibold">Veprime</th>
              </tr>
            </thead>
            <tbody>
              {dataQuery.isLoading && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-muted">
                    Duke ngarkuar…
                  </td>
                </tr>
              )}
              {!dataQuery.isLoading && articles.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-muted">
                    Nuk ka artikuj.
                  </td>
                </tr>
              )}
              {articles.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  {kind === 'shpenzimet' && (
                    <td className="px-4 py-2.5 text-muted">
                      {(a as ExpenseArticle).kategoria ?? '—'}
                    </td>
                  )}
                  <td className="px-4 py-2.5 font-medium">{a.artikulli}</td>
                  <td className="tnum px-4 py-2.5 text-right text-muted">
                    {formatNumber(a.usageCount)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(a as ExpenseArticle & RevenueArticle)}
                        className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                      >
                        Ndrysho
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setDeleting({ id: a.id, name: a.artikulli, usage: a.usageCount })
                        }
                        className="rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                      >
                        Fshij
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
          <form
            onSubmit={handleSave}
            className="w-full max-w-md rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl"
          >
            <h2 className="mb-4 text-lg font-semibold">
              {editing.id ? 'Ndrysho artikullin' : 'Shto artikull'}
            </h2>

            <div className="space-y-4">
              {kind === 'shpenzimet' && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="nk-kategoria">
                    Kategoria
                  </label>
                  <select
                    id="nk-kategoria"
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value === '' ? '' : Number(e.target.value))}
                    required
                    className={`${inputClass} bg-white`}
                  >
                    <option value="">Zgjidhni…</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.kategoria}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="nk-emri">
                  Emri i artikullit
                </label>
                <input
                  id="nk-emri"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className={inputClass}
                />
              </div>
            </div>

            {formError && (
              <div role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {formError}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Anulo
              </button>
              <button type="submit" className={btn.primary}>
                Ruaj
              </button>
            </div>
          </form>
        </div>
      )}

      {deleting && (
        <ConfirmDialog
          title="Konfirmo fshirjen"
          message={
            deleting.usage > 0
              ? `"${deleting.name}" përdoret në ${deleting.usage} rreshta. Fshirja nuk i heq ata rreshta — ata mbeten pa emër artikulli, por shumat e tyre vazhdojnë të numërohen në totale.`
              : `Fshi "${deleting.name}"? Nuk përdoret askund.`
          }
          confirmLabel={deleting.usage > 0 ? 'Fshij gjithsesi' : 'Fshij'}
          destructive
          busy={deleteMutation.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            deleteMutation.mutate({ id: deleting.id, force: deleting.usage > 0 });
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}
